import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../../styles/scss/externalEvents.scss";
import { slashAppExternalEvents } from "../../../utils/slashAppExternalEvents";

const DOW_JP = ["日", "月", "火", "水", "木", "金", "土"];

const pad = (n) => String(n).padStart(2, "0");
const toYmd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayYmd = () => {
  const t = new Date();
  return toYmd(t.getFullYear(), t.getMonth(), t.getDate());
};
function shortDate(s) {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  const wd = DOW_JP[new Date(y, m - 1, d).getDay()];
  return `${m}/${d}(${wd})`;
}
function fullDate(s) {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  const wd = DOW_JP[new Date(y, m - 1, d).getDay()];
  return `${y}年${m}月${d}日(${wd})`;
}

const STATUS = {
  pending: { cls: "st-pending", txt: "申請中" },
  approved: { cls: "st-approved", txt: "許可" },
  rejected: { cls: "st-rejected", txt: "却下" },
};
const st = (s) => STATUS[s] || { cls: "st-pending", txt: s };

// 単価（出演料・リハ費など）。未入力は「—」、0 は「無料」。
const yen = (v) =>
  v === null || v === undefined ? "—" : v === 0 ? "無料" : `¥${v.toLocaleString("ja-JP")}`;

// 累計額。0 は「無料」ではなく ¥0（まだ支払っていない、の意）。
const yenTotal = (v) => `¥${(v ?? 0).toLocaleString("ja-JP")}`;

// 出演生徒の表示（募集前は人数のみ）。
const performerSummary = (a) =>
  a.recruitment_status === "before"
    ? `募集前（出演予定 ${a.expected_performer_count ?? 0}名）`
    : `出演${a.performers.length}名`;

export default function ExternalEventsPage() {
  const [apps, setApps] = useState([]);
  // 共有リンク（?key=）で開かれた場合は未ログインの閲覧専用モード。
  // データは shared エンドポイントを鍵で叩き、承認/却下は不可（ボタンは押せない）。
  const shareKey = useMemo(
    () => new URLSearchParams(window.location.search).get("key") || "",
    []
  );
  const shareMode = Boolean(shareKey);
  const [loadError, setLoadError] = useState("");
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-based
  const [selDate, setSelDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null); // null | pending | approved | rejected
  const [modalApp, setModalApp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const toastRef = useRef(null);
  const listRef = useRef(null);
  const todayRef = useRef(null);

  const tYmd = todayYmd();

  const showToast = useCallback((msg) => {
    setToast(msg);
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(""), 2600);
  }, []);

  const load = useCallback(async () => {
    try {
      const data = shareMode
        ? await slashAppExternalEvents.listShared(shareKey)
        : await slashAppExternalEvents.list();
      setApps(data.applications || []);
      setLoadError("");
    } catch (e) {
      console.error("外部イベント申請の取得に失敗しました", e);
      if (shareMode) {
        setLoadError(
          e.status === 403
            ? "この共有リンクは無効です。"
            : "申請の取得に失敗しました。"
        );
      }
    }
  }, [shareMode, shareKey]);
  useEffect(() => {
    load();
  }, [load]);

  // 出演生徒の「出演回数・累計金額」はサーバ側の集計値なので、許可/却下で変わる。
  // 再取得したら、開いているモーダルの中身も新しい行に差し替える。
  useEffect(() => {
    setModalApp((prev) => (prev ? apps.find((a) => a.id === prev.id) || prev : prev));
  }, [apps]);

  // モーダルを開くときは最新値を取り直す（他の管理者の許可/却下も反映される）。
  const openModal = (app) => {
    setModalApp(app);
    load();
  };

  // カレンダーのセル（前方の空白 + 各日 + その日の申請）
  const calendarCells = useMemo(() => {
    const first = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    const byDate = {};
    apps.forEach((a) => {
      (byDate[a.event_start_date] = byDate[a.event_start_date] || []).push(a);
    });
    const cells = [];
    for (let i = 0; i < first; i++) cells.push({ blank: true, key: `b${i}` });
    for (let d = 1; d <= days; d++) {
      const ds = toYmd(year, month, d);
      cells.push({ key: ds, d, ds, items: byDate[ds] || [] });
    }
    return cells;
  }, [apps, year, month]);

  // ステータス別件数（統計タイル用）。
  const summary = useMemo(() => {
    const s = { pending: 0, approved: 0, rejected: 0 };
    apps.forEach((a) => {
      if (s[a.status] != null) s[a.status] += 1;
    });
    return s;
  }, [apps]);

  // 一覧: selDate があればその日のみ、statusFilter があればそのステータスのみ。
  const listRows = useMemo(() => {
    let list = [...apps];
    if (selDate) list = list.filter((a) => a.event_start_date === selDate);
    if (statusFilter) list = list.filter((a) => a.status === statusFilter);
    list.sort(
      (a, b) => a.event_start_date.localeCompare(b.event_start_date) || a.id - b.id
    );
    return list;
  }, [apps, selDate, statusFilter]);

  // 統計タイル押下でステータス絞り込みをトグル（日付絞り込みは解除）。
  const toggleStatusFilter = (s) => {
    setSelDate(null);
    setStatusFilter((prev) => (prev === s ? null : s));
  };

  // フィルタなしのとき「今日以降の先頭」を一番上に来るようスクロール（過去は上へ）。
  const firstFutureId = useMemo(() => {
    const f = listRows.find((a) => a.event_start_date >= tYmd);
    return f ? f.id : null;
  }, [listRows, tYmd]);

  useEffect(() => {
    if (selDate || statusFilter) return;
    if (todayRef.current && listRef.current) {
      listRef.current.scrollTop = todayRef.current.offsetTop;
    }
  }, [listRows, selDate, statusFilter, firstFutureId]);

  const prevMonth = () => {
    setSelDate(null);
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    setSelDate(null);
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else setMonth((m) => m + 1);
  };
  const goThisMonth = () => {
    setSelDate(null);
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  const setStatus = async (app, status) => {
    if (saving || shareMode) return; // 共有リンクの閲覧専用モードでは変更不可
    setSaving(true);
    try {
      const data = await slashAppExternalEvents.review(app.id, status);
      const patch = {
        status: data.status,
        status_label: data.status_label,
        reviewed_at: data.reviewed_at,
      };
      setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, ...patch } : a)));
      // 許可/却下したらモーダルを閉じる（結果はトーストと一覧・カレンダーの色で分かる）。
      setModalApp((prev) => (prev && prev.id === app.id ? null : prev));
      showToast(status === "approved" ? "許可しました" : "却下しました");
      // 出演生徒の出演回数・累計金額はサーバ側で集計しており、この許可/却下で
      // （同じ生徒が出ている他の申請も含めて）値が変わるので、一覧を取り直す。
      load();
    } catch (e) {
      // 失敗時はモーダルを開いたままにして、やり直せるようにする。
      showToast(e.message || "更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="external-events-page">
      <div className="ee-head">
        <h1>外部イベント出演申請</h1>
        <div className="ee-summary">
          <button
            type="button"
            className={`ee-stat st-pending ${statusFilter === "pending" ? "active" : ""}`}
            onClick={() => toggleStatusFilter("pending")}
          >
            <span className="ee-num">{summary.pending}</span>
            <span className="ee-lbl">申請中</span>
          </button>
          <button
            type="button"
            className={`ee-stat st-approved ${statusFilter === "approved" ? "active" : ""}`}
            onClick={() => toggleStatusFilter("approved")}
          >
            <span className="ee-num">{summary.approved}</span>
            <span className="ee-lbl">許可</span>
          </button>
          <button
            type="button"
            className={`ee-stat st-rejected ${statusFilter === "rejected" ? "active" : ""}`}
            onClick={() => toggleStatusFilter("rejected")}
          >
            <span className="ee-num">{summary.rejected}</span>
            <span className="ee-lbl">却下</span>
          </button>
        </div>
      </div>

      <div className="ee-toolbar">
        <button className="ee-btn ghost" onClick={prevMonth} aria-label="前の月">
          ‹
        </button>
        <input
          type="month"
          value={`${year}-${pad(month + 1)}`}
          onChange={(e) => {
            const [y, m] = e.target.value.split("-").map(Number);
            if (y && m) {
              setSelDate(null);
              setYear(y);
              setMonth(m - 1);
            }
          }}
        />
        <button className="ee-btn ghost" onClick={nextMonth} aria-label="次の月">
          ›
        </button>
        <button className="ee-btn ghost" onClick={goThisMonth}>
          今月
        </button>
        <span className="ee-spacer" />
        {selDate && (
          <button className="ee-btn ghost" onClick={() => setSelDate(null)}>
            絞り込み解除（{shortDate(selDate)}）
          </button>
        )}
      </div>

      <div className="ee-grid">
        {/* LEFT: カレンダー */}
        <div className="ee-card">
          <div className="ee-card-head">
            <h3>イベントカレンダー</h3>
            <div className="ee-legend">
              <span className="ee-tag st-pending">申請中</span>
              <span className="ee-tag st-approved">許可</span>
              <span className="ee-tag st-rejected">却下</span>
            </div>
          </div>
          <div className="ee-cal-scroll">
            <div className="ee-cal">
              <div className="ee-cal-dow">
                {DOW_JP.map((d, i) => (
                  <div key={d} className={`ee-dow ${i === 0 ? "sun" : i === 6 ? "sat" : ""}`}>
                    {d}
                  </div>
                ))}
              </div>
              <div className="ee-cal-body">
                {calendarCells.map((c) =>
                  c.blank ? (
                    <div key={c.key} className="ee-cell blank" />
                  ) : (
                    <div
                      key={c.key}
                      className={`ee-cell ${c.ds === tYmd ? "today" : ""} ${
                        c.ds === selDate ? "sel" : ""
                      }`}
                      onClick={() => {
                        setStatusFilter(null);
                        setSelDate(c.ds === selDate ? null : c.ds);
                      }}
                    >
                      <div className="ee-daynum">
                        {c.d}
                        {c.ds === tYmd && <span className="ee-today-tag">今日</span>}
                      </div>
                      {c.items.map((a) => (
                        <div
                          key={a.id}
                          className={`ee-chip ${st(a.status).cls}`}
                          title={`${a.event_name}（${st(a.status).txt}）`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setStatusFilter(null);
                            setSelDate(c.ds);
                          }}
                        >
                          <span className="ee-chip-name">{a.event_name}</span>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: 一覧 */}
        <div className="ee-card">
          <div className="ee-card-head">
            <h3>
              申請一覧
              {selDate
                ? `（${shortDate(selDate)}）`
                : statusFilter
                ? `（${st(statusFilter).txt}）`
                : ""}
            </h3>
            <span className="ee-count">{listRows.length}件</span>
          </div>
          <div className="ee-list" ref={listRef}>
            {loadError ? (
              <div className="ee-list-empty">{loadError}</div>
            ) : (
              listRows.length === 0 && (
                <div className="ee-list-empty">該当する申請はありません</div>
              )
            )}
            {listRows.map((a) => {
              const isFutureHead =
                !selDate && !statusFilter && a.id === firstFutureId;
              return (
                <div
                  key={a.id}
                  ref={isFutureHead ? todayRef : null}
                  className={`ee-row ${a.event_start_date < tYmd ? "past" : ""}`}
                  onClick={() => openModal(a)}
                >
                  <div className="ee-row-date">{shortDate(a.event_start_date)}</div>
                  <div className="ee-row-main">
                    <div className="ee-row-name">{a.event_name}</div>
                    <div className="ee-row-sub">
                      {a.instructor_name} ・ {performerSummary(a)}
                    </div>
                  </div>
                  <span className={`ee-pill ${st(a.status).cls}`}>{st(a.status).txt}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 詳細モーダル */}
      {modalApp && (
        <div
          className="ee-overlay"
          onClick={(e) => e.target === e.currentTarget && setModalApp(null)}
        >
          <div className="ee-modal" role="dialog" aria-modal="true">
            <div className="ee-modal-head">
              <h3>{modalApp.event_name}</h3>
              <button className="ee-x" onClick={() => setModalApp(null)} aria-label="閉じる">
                ×
              </button>
            </div>
            <div className="ee-modal-body">
              <div className="ee-status-row">
                <span className={`ee-pill lg ${st(modalApp.status).cls}`}>
                  {st(modalApp.status).txt}
                </span>
              </div>
              <dl className="ee-detail">
                <div>
                  <dt>開催日</dt>
                  <dd>{fullDate(modalApp.event_start_date)}</dd>
                </div>
                <div>
                  <dt>申請者</dt>
                  <dd>{modalApp.instructor_name || "—"}</dd>
                </div>
                {modalApp.event_url ? (
                  <div>
                    <dt>URL</dt>
                    <dd>
                      <a
                        className="ee-ig"
                        href={modalApp.event_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {modalApp.event_url}
                      </a>
                    </dd>
                  </div>
                ) : null}
                {modalApp.event_instagram ? (
                  <div>
                    <dt>Instagram</dt>
                    <dd>
                      <a
                        className="ee-ig"
                        href={`https://instagram.com/${modalApp.event_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        @{modalApp.event_instagram}
                      </a>
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>アーティスト写真</dt>
                  <dd>{modalApp.use_artist_photo ? "使用する" : "使用しない"}</dd>
                </div>
                {/* 金額はすべて主催者に支払われるもの（当社は関与しない）。 */}
                <div>
                  <dt>出演料</dt>
                  <dd>
                    {yen(modalApp.instructor_fee)}
                    <span className="ee-fee-note">インストラクター負担</span>
                  </dd>
                </div>
                <div>
                  <dt>生徒1人あたり</dt>
                  <dd>{yen(modalApp.student_fee)}</dd>
                </div>
                <div>
                  <dt>リハーサル</dt>
                  <dd>
                    {yen(modalApp.rehearsal_fee_per_student)}
                    <span className="ee-fee-note">1回につき生徒から徴収</span>
                  </dd>
                </div>
                {modalApp.note ? (
                  <div>
                    <dt>補足</dt>
                    <dd className="ee-note">{modalApp.note}</dd>
                  </div>
                ) : null}
              </dl>

              {modalApp.recruitment_status === "before" ? (
                <>
                  <div className="ee-perf-head">出演生徒</div>
                  <div className="ee-perf-empty">
                    まだ募集前です（出演予定 {modalApp.expected_performer_count ?? 0}名）。
                    生徒が決まるとインストラクターが登録します。
                  </div>
                </>
              ) : (
                <>
                  <div className="ee-perf-head">
                    出演生徒（{modalApp.performers.length}名）
                  </div>
                  <ul className="ee-perf-list">
                    {modalApp.performers.map((p) => (
                      <li key={p.id}>
                        <div className="ee-perf-main">
                          <span className="ee-perf-name">{p.student_name}</span>
                          {/* 外部イベント出演実績（承認済みの申請すべて。開催日は問わない）。
                              この申請自身は除いた数値が slash-app から返る。 */}
                          <span className="ee-perf-stats">
                            外部イベント出演 {p.appearance_count ?? 0}回 ・ 累計{" "}
                            {yenTotal(p.total_student_fee_yen)}
                          </span>
                        </div>
                        {p.instagram_account ? (
                          <a
                            className="ee-ig"
                            href={`https://instagram.com/${p.instagram_account}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            @{p.instagram_account}
                          </a>
                        ) : (
                          <span className="ee-ig none">—</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="ee-modal-foot">
              <div className="right">
                <button
                  className={`ee-btn danger ${modalApp.status === "rejected" ? "active" : ""}`}
                  disabled={saving || shareMode}
                  title={shareMode ? "閲覧専用リンクのため操作できません" : undefined}
                  onClick={() => setStatus(modalApp, "rejected")}
                >
                  却下
                </button>
                <button
                  className={`ee-btn primary ${modalApp.status === "approved" ? "active" : ""}`}
                  disabled={saving || shareMode}
                  title={shareMode ? "閲覧専用リンクのため操作できません" : undefined}
                  onClick={() => setStatus(modalApp, "approved")}
                >
                  許可
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="ee-toast show">{toast}</div>}
    </div>
  );
}
